import { ofetch, type $Fetch } from 'ofetch'
import { IS_DEV, API_BASE_URL } from '@/shared/config'
import { ApiError, type ApiErrorData } from './types'

interface HttpClientConfig {
  getAccessToken: () => string | null
  onAuthFailure: () => void
}

let clientConfig: HttpClientConfig | null = null

/**
 * Configure auth handlers for all API clients.
 * Must be called once during app initialization.
 */
export function configureHttpClient(config: HttpClientConfig): void {
  clientConfig = config
}

/**
 * Create an API client for a specific domain.
 * All clients share auth configuration.
 */
export function createApiClient(baseURL: string, options?: { withAuth?: boolean }): $Fetch {
  const withAuth = options?.withAuth ?? true

  return ofetch.create({
    baseURL,
    retry: false,

    async onRequest({ options }) {
      if (withAuth && clientConfig) {
        const token = clientConfig.getAccessToken()

        // Proactive refresh if token is expiring soon
        // if (token && clientConfig.isTokenExpiringSoon()) {
        //   try {
        //     token = await clientConfig.onRefreshNeeded()
        //   } catch {
        //     // Refresh failed, proceed with current token
        //   }
        // }

        if (token) {
          const headers = new Headers(options.headers)
          headers.set('Authorization', `Bearer ${token}`)
          options.headers = headers
        }
      }

      if (IS_DEV) {
        console.debug(`[HTTP] ${options.method ?? 'GET'} ${baseURL}`)
      }
    },

    onResponseError({ response }) {
      // Handle 401 as auth failure
      if (withAuth && clientConfig && response.status === 401) {
        clientConfig.onAuthFailure()
      }

      const data = response._data as ApiErrorData | undefined

      throw new ApiError(response.status, {
        code: data?.code || `HTTP_${response.status}`,
        message: data?.message || response.statusText || 'Request failed',
        details: data?.details,
      })
    },
  })
}

export const client = createApiClient(API_BASE_URL)
export const publicClient = createApiClient(API_BASE_URL, { withAuth: false })
